// src/components/recipient/RecipientCompletion.tsx
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, Repeat, ChevronRight } from "lucide-react";
import logo_default from "../../assets/img/logo.png";
import logo_gold from "../../assets/img/logo_gold.png";
import { CampaignCopy } from "../../campaigns/types/campaign.types";

// Define the interface locally since it might not be exported
interface CompletionConfig {
  hasBouquet: boolean;
  isPreview: boolean;
  nextIsPay: boolean;
  primaryColor: string;
  secondaryColor: string;
  recipientName: string;
  senderName: string;
  cardId: string;
  archetype?: string;
  theme?: any;
  campaignCopy?: CampaignCopy;
}

interface RecipientCompletionProps {
  config: CompletionConfig;
  onCreateOwn: () => void;
  onReplay: () => void;
}

export const RecipientCompletion: React.FC<RecipientCompletionProps> = ({
  config,
  onCreateOwn,
  onReplay,
}) => {
  const [showReplay, setShowReplay] = useState(false);

  const primary = config.primaryColor;
  const secondary = config.secondaryColor;
  const hasBouquet = config.hasBouquet;

  // Delayed reveal of options - let the moment breathe first
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowReplay(true);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto"
      style={{
        background: `radial-gradient(circle at center, ${primary}30 0%, ${secondary}80 100%)`,
      }}
    >
      {/* Gentle floating confetti */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            initial={{
              x: Math.random() * (typeof window !== "undefined" ? window.innerWidth : 1000),
              y: -20,
              opacity: 0.8,
              scale: Math.random() * 0.5 + 0.5,
            }}
            animate={{
              y: (typeof window !== "undefined" ? window.innerHeight : 1000) + 100,
              rotate: Math.random() * 360,
              x: (Math.random() - 0.5) * 200,
            }}
            transition={{
              duration: 5 + Math.random() * 5,
              repeat: Infinity,
              delay: Math.random() * 5,
              ease: "linear",
            }}
            className="absolute w-2 h-2 rounded-full"
            style={{
              backgroundColor: i % 3 === 0 ? primary : i % 3 === 1 ? "#fff" : secondary,
              opacity: 0.3,
            }}
          />
        ))}
      </div>

      <div className="w-full max-w-md py-8 md:py-12 z-10 flex flex-col items-center">
        {/* LOGO CONTAINER: Centers the child and provides 3D depth */}
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 260, damping: 20 }}
          className="relative mb-8 flex justify-center items-center perspective-[1000px]"
        >
          {/* Ambient Glow behind the spinning logo */}
          <div
            className="absolute inset-0 blur-3xl opacity-30 scale-150 rounded-full"
            style={{ backgroundColor: hasBouquet ? "#FFD700" : primary }}
          />

          <motion.div className="relative w-24 h-24 md:w-32 md:h-32">
            <img
              src={hasBouquet ? logo_gold : logo_default}
              alt="LRNSY"
              className="w-full h-full object-contain drop-shadow-[0_0_15px_rgba(255,255,255,0.3)]"
            />
          </motion.div>
        </motion.div>

        {/* Completion message */}
        <motion.h2
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="text-2xl sm:text-3xl md:text-4xl font-black text-white mb-3 md:mb-4 font-display text-center px-4"
        >
          {config.campaignCopy?.completionHeadline ||
            (config.isPreview && !config.nextIsPay
              ? "This Is How It Feels"
              : config.isPreview && config.nextIsPay
                ? "Your Moment Is Ready"
                : hasBouquet
                  ? "You Were Thought About"
                  : "A Moment Found You")}
        </motion.h2>

        {/* Personal touch */}
        <motion.p
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-white/70 mb-6 md:mb-8 px-4 text-center text-sm sm:text-base font-light"
        >
          {
            /* ---------- PREVIEW BEFORE CREATE ---------- */

            config.isPreview && !config.nextIsPay
              ? "Every LRNSY moment is designed to pause someone’s day, and remind them they matter."
              : /* ---------- PREVIEW → PAYMENT NEXT ---------- */

                config.isPreview && config.nextIsPay
                ? "Everything is ready. Secure this moment now, and let it reach the person it was meant for."
                : /* ---------- RECIPIENT RECEIVES ---------- */

                  hasBouquet
                  ? `${config.senderName} paused their world for you. Sometimes the smallest gestures arrive exactly when they’re needed.`
                  : `${
                      config.campaignCopy?.completionSubtitle ||
                      `Someone chose to make this moment for ${config.recipientName}.`
                    }`
          }
        </motion.p>

        {/* Main CTA - appears after emotional moment */}
        <AnimatePresence>
          {showReplay && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="space-y-4"
            >
              <motion.button
                onClick={onCreateOwn}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                className="w-full sm:w-auto px-8 py-4 bg-white text-black rounded-full font-bold hover:shadow-2xl transition-all flex items-center justify-center gap-3 mx-auto"
                style={{
                  boxShadow: `0 20px 30px -10px ${primary}80`,
                }}
              >
                <Heart size={20} className="fill-primary text-primary" />
                <span>
                  {!config.nextIsPay ? `Create Someone’s Moment` : "Make This Moment Real"}
                </span>
                <ChevronRight size={18} />
              </motion.button>

              {/* Secondary options */}
              <div className="flex flex-wrap justify-center gap-3 pt-4">
                {!config.isPreview && (
                  <button
                    onClick={onReplay}
                    className="px-6 py-3 rounded-full bg-white/10 backdrop-blur-sm text-white/80 hover:bg-white/20 transition-all text-sm flex items-center gap-2"
                  >
                    <Repeat size={14} />
                    <span>Relive This Moment</span>
                  </button>
                )}

                {/* <button
                  onClick={handleShare}
                  className="px-6 py-3 rounded-full bg-white/10 backdrop-blur-sm text-white/80 hover:bg-white/20 transition-all text-sm flex items-center gap-2"
                >
                  <Share2 size={14} />
                  <span>Share Story</span>
                </button> */}

                {/* <button
                  onClick={handleCreateHighlight}
                  className="px-6 py-3 rounded-full bg-white/10 backdrop-blur-sm text-white/80 hover:bg-white/20 transition-all text-sm flex items-center gap-2"
                >
                  <Camera size={14} />
                  <span>Capture Reaction</span>
                </button> */}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Share options modal - simplified */}
        {/* <AnimatePresence>
          {showShareOptions && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="fixed inset-x-0 bottom-0 p-6 bg-black/90 backdrop-blur-xl rounded-t-3xl border-t border-white/10"
            >
              <h3 className="text-white text-lg font-bold mb-4">
                Share This Feeling
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <button className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-white/5">
                  <div className="w-12 h-12 rounded-full bg-[#25D366]/20 flex items-center justify-center">
                    <span className="text-2xl">📱</span>
                  </div>
                  <span className="text-white/60 text-xs">WhatsApp</span>
                </button>
                <button className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-white/5">
                  <div className="w-12 h-12 rounded-full bg-[#00acee]/20 flex items-center justify-center">
                    <span className="text-2xl">🐦</span>
                  </div>
                  <span className="text-white/60 text-xs">Twitter</span>
                </button>
                <button className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-white/5">
                  <div className="w-12 h-12 rounded-full bg-[#E4405F]/20 flex items-center justify-center">
                    <span className="text-2xl">📸</span>
                  </div>
                  <span className="text-white/60 text-xs">Instagram</span>
                </button>
              </div>
              <button
                onClick={() => setShowShareOptions(false)}
                className="mt-4 w-full py-3 text-white/60 text-sm"
              >
                Maybe Later
              </button>
            </motion.div>
          )}
        </AnimatePresence> */}
      </div>

      {/* LRNSY signature */}
      <div className="absolute bottom-4 opacity-20 text-[8px] tracking-[0.5em] text-white/30">
        LRNSY
      </div>
    </motion.div>
  );
};
